/**
 * @file FileUpload.jsx
 * @description Componente de upload de arquivos com drag & drop, preview e validação. Possui limite de envio
 * Só permite PDF, doc e markdown.
 * 
 * @exports FileUpload - Componente de upload
 * 
 * @example
 * <FileUpload 
 *   onFileSelect={handleFile}
 *   accept=".pdf,.doc"
 *   maxSize={5 * 1024 * 1024}
 * />
 */

import { useState, useRef, useEffect } from 'react';
import { HiUpload } from 'react-icons/hi';
import { AiOutlineFile } from 'react-icons/ai';
import { MdDelete, MdClose } from 'react-icons/md';
import './FileUpload.css';

const Spinner = () => (
  <div className="file-upload__loading-spinner">
    <div className="spinner"></div>
    <span>Uploading...</span>
  </div>
);

export const FileUpload = ({
  onFileSelect,
  isLoading = false, 
  initialFile = null,
  onCancelUpload,
  accept = '.txt,.eml,.html,.md,.json,.csv,.doc,.docx,.ppt,.pptx,.pdf,.xls,.xlsx,.jpg,.jpeg,.bmp,.png,.tiff,.mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm,.mkv',
  supportedTypes = 'PDF, text, Markdown, Audio .mp3',
  multiple = false,
  className = '',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(initialFile);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUploadedFile(initialFile);
  }, [initialFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileInputChange = (e) => {
    if (isLoading) return;
    const files = e.target.files;
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFiles = (files) => {
    const file = files[0];
    setUploadedFile(file);
    if (onFileSelect) {
      onFileSelect(file); 
    }
  };

  const handleDelete = () => {
    if (isLoading) return;
    setUploadedFile(null); 
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const handleCancelUpload = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onCancelUpload) {
      onCancelUpload();
    }
  };

  const handleClick = () => {
    if (isLoading) return;
    fileInputRef.current?.click();
  };

  return (
    <div className={`file-upload ${className} ${isLoading ? 'file-upload--loading' : ''}`}>
      {!uploadedFile && !isLoading ? (
        <div
          className={`file-upload__dropzone ${isDragging ? 'file-upload__dropzone--dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            multiple={multiple}
            className="file-upload__input"
            disabled={isLoading}
          />
          <div className="file-upload__icon">
            <HiUpload size={10} />
            <p className="file-upload__text"> Upload source file</p>
          </div>
          <p className="file-upload__subtext">
            Drag and drop or select the file to upload.
          </p>
          <p className="file-upload__subtext">
            Supported file types: {supportedTypes}
          </p>
        </div>
      ) : (
        <div className="file-upload-workspace">
          <div className="file-upload__preview-card">
            <div className="file-upload__preview-workspace">
              <div className="file-upload__file-info">
                <div className="file-upload__file-icon" aria-hidden>
                  <AiOutlineFile size={24} />
                </div>
                <div className="file-upload__file-details">
                  <p className="file-upload__file-name">{uploadedFile.name}</p>
                </div>
              </div>

              {isLoading ? (
                <div className="file-upload__preview-actions">
                  <div className="loading-cancel-box">
                    <Spinner />
                    <button
                      type="button"
                      className="file-upload__cancel-btn"
                      onClick={handleCancelUpload}
                      aria-label="Cancel upload"
                    >
                      <MdClose size={20} />
                      Cancel upload
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload__preview-actions">
                  <button
                    type="button"
                    className="file-upload__delete"
                    onClick={handleDelete}
                    aria-label="Delete file"
                  >
                    <MdDelete size={20} />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};